import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames/bind";
import { IonCard, IonCardContent, IonCardHeader, IonCardSubtitle } from "@ionic/react";
import { Product as TProduct } from "@core/types";
import styles from "../../../components/ProductDetail/ProductDetail.module.scss";
import { getLowestPriceVariation } from "../../shared/utils";
import { AttributeBadge } from "../../shared/AttributeBadge/AttributeBadge";
import { useConfig } from "../../../providers/config";

type TAttributes = { [k: string]: Set<string> };

interface ISelectVariationAttributeProps {
  variations: TProduct[];
  onVariationSelect: (variation: TProduct | null) => void;
}

const cx = classNames.bind(styles);

const SelectVariationAttribute: React.FC<ISelectVariationAttributeProps> = ({ variations, onVariationSelect }) => {
  const [chosenAttributes, setChosenAttributes] = useState<Record<string, string>>();
  const { ignoredProductAttributes } = useConfig();

  const { attributes, instockVariations } = useMemo(() => {
    const attributes: TAttributes = {};

    variations?.forEach((variation) => {
      variation.attributes?.forEach((attribute) => {
        if (
          ignoredProductAttributes &&
          ignoredProductAttributes.some((ignoredAttr) => ignoredAttr.toLowerCase() === attribute.name.toLowerCase())
        ) {
          return;
        }

        if (!attributes[attribute.name]) {
          attributes[attribute.name] = new Set();
        }

        if (attribute.option) {
          attributes[attribute.name].add(attribute.option);
        } else if (attribute.options) {
          attribute.options.forEach((option) => {
            attributes[attribute.name].add(option);
          });
        }
      });
    });

    const instockVariations = variations.filter((variation) => variation.stockStatus === "instock");
    return { attributes, instockVariations };
  }, [variations]);

  const handleClick = (attributeName: string, value: string) => {
    if (
      chosenAttributes &&
      chosenAttributes.hasOwnProperty(attributeName) &&
      chosenAttributes[attributeName] === value
    ) {
      const newAttrs = Object.assign({}, chosenAttributes);
      delete newAttrs[attributeName];
      setChosenAttributes(newAttrs);
    } else {
      setChosenAttributes({ ...chosenAttributes, [attributeName]: value });
    }
  };

  const isAttributeChosen = (name: string, value: string) => {
    if (chosenAttributes && chosenAttributes.hasOwnProperty(name)) {
      return chosenAttributes[name] === value;
    }
  };

  const availableVariationAttributes = useMemo(() => {
    return instockVariations.map((variation) => {
      const attrs =
        variation.attributes?.map(({ name, option }) => {
          return { [name]: option };
        }) ?? [];

      return attrs.reduce((acc, attr) => {
        return { ...acc, ...attr };
      }, {});
    });
  }, [instockVariations]);

  const isAttributeAvailable = (name: string, value: string) => {
    const newAttributes = { ...chosenAttributes, [name]: value };
    return availableVariationAttributes.some((variationAttributes) => {
      return Object.keys(newAttributes).every((newAttributeName) => {
        return variationAttributes[newAttributeName] === newAttributes[newAttributeName];
      });
    });
  };

  const changeVariation = () => {
    const newSelectVariation = instockVariations.find((variation) => {
      return variation.attributes?.every((variationAttribute) => {
        if (chosenAttributes) {
          // only for ts skipping, it is always true
          return variationAttribute.option === chosenAttributes[variationAttribute.name];
        }
      });
    });

    if (newSelectVariation) {
      onVariationSelect(newSelectVariation);
    }
  };

  useEffect(() => {
    if (!chosenAttributes) {
      const lowestPriceVariationAttributes = getLowestPriceVariation(variations)?.attributes?.reduce((acc, attr) => {
        return { ...acc, [attr.name]: attr.option ?? "" };
      }, {});
      if (lowestPriceVariationAttributes && Object.keys(lowestPriceVariationAttributes).length) {
        setChosenAttributes(lowestPriceVariationAttributes); // use state on use effect
      }
    }
  }, [variations]);

  useEffect(() => {
    changeVariation();
  }, [chosenAttributes]);

  return (
    <>
      {attributes &&
        Object.keys(attributes).map((attributeName) => (
          <IonCard className={cx("attributesCard")} key={attributeName}>
            <IonCardHeader className={cx("attributesCardHeader")}>
              <IonCardSubtitle>{attributeName}</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent className={cx("attributesCardContent")}>
              {[...attributes[attributeName]].map((value) => {
                const isCurrentAttributeAvailable = isAttributeAvailable(attributeName, value);

                return (
                  <span
                    key={value}
                    className={cx("attributesCardSpan", {
                      attributeIsDisabled: !isCurrentAttributeAvailable,
                    })}
                  >
                    <AttributeBadge
                      value={value}
                      isDisabled={!isCurrentAttributeAvailable}
                      isColorPrimary={!!isAttributeChosen(attributeName, value)}
                      onClick={() => {
                        if (isCurrentAttributeAvailable) {
                          handleClick(attributeName, value);
                        }
                      }}
                    />
                  </span>
                );
              })}
            </IonCardContent>
          </IonCard>
        ))}
    </>
  );
};

export { SelectVariationAttribute };
